import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import moment from 'moment'

import {
  getVehicles,
  addVehicle,
  updateVehicle,
} from '../actions/vehicleActions'
import Button from '../components/Button'
import Input from '../components/Input'
import Loading from '../components/Loading'
import MatTable from '../components/MatTable'
import {
  GET_VEHICLES_RESET,
  ADD_VEHICLE_RESET,
  UPDATE_VEHICLE_RESET,
} from '../constants/vehicleConstants'

const Vehicle = ({ history }) => {
  // * States
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [addVehicleModal, setAddVehicleModal] = useState(false)
  const [name, setName] = useState('')
  const [id, setId] = useState('')
  const [type, setType] = useState('')

  // * Initialization
  const dispatch = useDispatch()

  // * Check for auth
  const adminLogin = useSelector((state) => state.adminLogin)
  const { adminInfo } = adminLogin

  useEffect(() => {
    // * Check if user info exists
    if (!adminInfo) {
      history.push('/')
    } else {
      if (adminInfo.loginType !== 'Unorganised') {
        history.push('/')
      }
    }
  }, [adminInfo, history])

  useEffect(() => {
    setLoading(true)
    dispatch(getVehicles())
  }, [])

  // * Get Vehicles
  const getVehiclesInfo = useSelector((state) => state.getVehiclesInfo)
  const { errorGetVehicles, getVehiclesData } = getVehiclesInfo

  useEffect(() => {
    dispatch({ type: GET_VEHICLES_RESET })
    if (getVehiclesData) {
      setLoading(false)
      setData(getVehiclesData)
    } else if (errorGetVehicles) {
      setLoading(false)
      toast(errorGetVehicles, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [getVehiclesData, errorGetVehicles])

  const closeAddVehicleModal = () => {
    setType('')
    setAddVehicleModal(false)
    setName('')
    setId('')
  }

  const addVehicleHandler = () => {
    if (!name) {
      toast('All the fields are mandatory', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    } else {
      dispatch(addVehicle(name))
    }
  }

  // * Add Vehicle
  const addVehicleInfo = useSelector((state) => state.addVehicleInfo)
  const { loadingAddVehicle, errorAddVehicle, addVehicleData } = addVehicleInfo

  useEffect(() => {
    dispatch({ type: ADD_VEHICLE_RESET })
    if (addVehicleData) {
      closeAddVehicleModal()
      toast(addVehicleData.msg, {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setTimeout(() => {
        dispatch(getVehicles())
      }, 1000)
    } else if (errorAddVehicle) {
      toast(errorAddVehicle, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [addVehicleData, errorAddVehicle])

  const updatedVehicle = (item) => {
    setId(item._id)
    setType('Update')
    setAddVehicleModal(true)
    setName(item.name)
  }

  const updateVehicleHandler = () => {
    if (!id) {
      toast('All the fields are mandatory', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    } else {
      dispatch(updateVehicle(id, name))
    }
  }

  // * Update Vehicle
  const updateVehicleInfo = useSelector((state) => state.updateVehicleInfo)
  const { loadingUpdateVehicle, errorUpdateVehicle, updateVehicleData } =
    updateVehicleInfo

  useEffect(() => {
    dispatch({ type: UPDATE_VEHICLE_RESET })
    if (updateVehicleData) {
      closeAddVehicleModal()
      toast(updateVehicleData.msg, {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setTimeout(() => {
        dispatch(getVehicles())
      }, 1000)
    } else if (errorUpdateVehicle) {
      toast(errorUpdateVehicle, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [updateVehicleData, errorUpdateVehicle])

  const headCells = [
    {
      field: 'name',
      title: 'Name',
      render: (rowData) => {
        return (
          <div
            className='font-bold text-md cursor-pointer text-blue-800 bg-blue-100 p-2 flex justify-center rounded'
            onClick={() => updatedVehicle(rowData)}
          >
            {rowData.name}
          </div>
        )
      },
    },
    {
      field: 'createdAt',
      title: 'Created At',
      render: (rowData) => {
        return <p>{moment(rowData.createdAt).format('YYYY-MM-DD HH:mm:ss')}</p>
      },
    },
  ]

  if (loading) {
    return <Loading />
  }

  return (
    <>
      <div className='w-full h-full'>
        <h1 className='text-2xl font-semibold'>Vehicles</h1>
        <div className='bg-white shadow-md rounded px-8 py-4 my-4'>
          <div className='flex justify-end'>
            <Button
              custom='py-2'
              type='button'
              onClick={() => {
                setType('Add')
                setAddVehicleModal(true)
              }}
              text='Add a Vehicle'
            />
          </div>
        </div>
        <MatTable headCells={headCells} data={data} type='Location' />
        {addVehicleModal && (
          <>
            <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
              <div className='relative w-auto my-6 mx-auto max-w-3xl'>
                {/*content*/}
                <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                  {/*header*/}
                  <div className='flex items-center justify-between py-3 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                    <h3 className='text-lg font-semibold'>
                      {type === 'Add' ? 'Add' : 'Update'} Vehicle
                    </h3>
                  </div>
                  {/*body*/}
                  <div className='relative p-6 flex-auto'>
                    <Input
                      width='flex-1 w-full mb-4'
                      name='Name *'
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                    <p className='text-sm'>
                      All the fields with * are mandatory
                    </p>
                  </div>
                  {/*footer*/}
                  <div className='flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b'>
                    <button
                      className='text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                      type='button'
                      onClick={closeAddVehicleModal}
                    >
                      Close
                    </button>
                    <button
                      className='bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300'
                      type='button'
                      onClick={
                        type === 'Add'
                          ? addVehicleHandler
                          : updateVehicleHandler
                      }
                      disabled={loadingAddVehicle || loadingUpdateVehicle}
                    >
                      {type === 'Add' ? 'Add' : 'Update'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
          </>
        )}
      </div>
    </>
  )
}

export default Vehicle
